import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../constants";
import logoDark from "../../../../assets/brand/logo_site.svg";
import logoLight from "../../../../assets/brand/logo_site_onlight.svg";
import logoPurple from "../../../../assets/brand/logo_site_purple.svg";
import FlagshipLiveEt from "./FlagshipLiveEt";
import { FlagshipEtTimeline } from "./FlagshipEtTimeline";
import FlagshipFormats from "./FlagshipFormats";
import { SelectClipsLoop, BlackCherryLoop } from "./FlagshipTwoWaysDemos";
import FlagshipPhoneToTimeline from "./FlagshipPhoneToTimeline";
import { DEMO_YOUTUBE_ID } from "./FlagshipPage";

/**
 * Home Layout "S" — the dedicated PREMIERE-PLUGIN letter: "Controlling Premiere Pro from your Phone!"
 *
 * Real page. The hero pairs a slide-toggle [📱 Phone ⇄ ▸ In Premiere]: the REAL cell-phone ET (you
 * cherry-pick right there) flips to a real Premiere screenshot (drop one at public/premiere-real.png)
 * showing the clips landed. Below: how it works, the real pick→timeline, pricing, close.
 *
 * ISOLATION: styles scoped under `.ccpp` (theme vars under `.ccpp[data-theme=...]`, on the root div).
 * Reuses the flagship-scoped ET copies; originals untouched. Additive; default = "A".
 */

const CheckSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5L20 6" />
  </svg>
);
const SunSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
  </svg>
);
const MoonSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);
const PrLogo: React.FC = () => (
  <span className="prlogo" aria-label="Premiere Pro">
    Pr
  </span>
);


type Theme = "light" | "dark";
const THEME_KEY = "cc_flagship_theme";

const PluginPage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>("light");
  const logo = theme === "light" ? logoLight : logoDark;

  useEffect(() => {
    try {
      const s = localStorage.getItem(THEME_KEY);
      if (s === "light" || s === "dark") setTheme(s);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const toggleTheme = () =>
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch (e) {
        /* ignore */
      }
      return next;
    });

  const scrollTo = (id: string) => {
    try {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      /* ignore */
    }
  };
  const goHome = () => navigate(ROUTES.FLAGSHIP);
  const goPricing = () => navigate(ROUTES.PRICING);
  const goPluginDownload = () => navigate(ROUTES.PLUGIN_DOWNLOAD);
  // scroll to the live editor AND drive it: CherryPick ticks a few clips, Black Cherry opens the AI panel
  const goTryAnd = (evt: string) => {
    scrollTo("try");
    try {
      window.dispatchEvent(new CustomEvent(evt));
    } catch (e) {
      /* ignore */
    }
  };

  const Brand: React.FC<{ size?: number }> = ({ size = 30 }) => (
    <>
      <img className="cherry-mark" src={logo} alt="" height={size} style={{ width: "auto" }} />
      <span className="wordmark">
        Cherry<span className="ck">Clipz</span>
      </span>
    </>
  );

  return (
    <div className="ccpp" data-theme={theme} id="ccpp-top">
      <style>{PLUGIN_CSS}</style>

      {/* NAV */}
      <nav>
        <div className="wrap nav-in">
          <button className="brand brand-btn" onClick={goHome} aria-label="CherryClipz home">
            <Brand size={30} />
          </button>
          <div className="nav-links">
            <button className="linkbtn" onClick={() => scrollTo("inpremiere")}>
              In Premiere
            </button>
            <button className="linkbtn" onClick={() => scrollTo("watch")}>
              Watch
            </button>
            <button className="linkbtn" onClick={() => scrollTo("how")}>
              How it works
            </button>
            <button className="linkbtn" onClick={() => scrollTo("try")}>
              Try it
            </button>
            <button className="linkbtn" onClick={() => scrollTo("formats")}>
              Formats
            </button>
            <button className="linkbtn" onClick={() => scrollTo("pricing")}>
              Pricing
            </button>
            <button className="linkbtn" onClick={() => navigate(ROUTES.MY_LIBRARY)}>
              My library
            </button>
            <button className="linkbtn" onClick={goHome}>
              Home
            </button>
          </div>
          <div className="nav-right">
            <button className="toggle" onClick={toggleTheme} aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} title="Light / dark">
              <span className="sun">
                <SunSvg />
              </span>
              <span className="moon">
                <MoonSvg />
              </span>
            </button>
            <button className="btn btn-ghost" onClick={() => scrollTo("try")}>
              Try it free
            </button>
            <button className="btn btn-cherry" onClick={goPluginDownload}>
              Get the panel
            </button>
          </div>
        </div>
      </nav>

      {/* HERO — pitch + slide toggle (phone ⇄ Premiere) */}
      <header className="hero" id="top">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <div className="lockup">
              <img className="cc-mark" src={logo} alt="CherryClipz" height={56} style={{ width: "auto" }} />
              <span className="plus">+</span>
              <PrLogo />
            </div>
            <span className="eyebrow">Premiere Pro plugin</span>
            <h1>
              Control Premiere Pro
              <br />
              from your <span className="ink-cherry">phone</span>.
            </h1>
            <p className="pocket mono">Premiere Pro in your pocket · cut from your couch.</p>
            <p className="sub">
              Tick a clip in the spreadsheet on your phone and it lands on your Premiere timeline — a native,
              full-quality clip, live, in about a second.
            </p>
            <div className="cta">
              <button className="btn btn-cherry" onClick={() => scrollTo("try")}>
                Try it free
              </button>
              <button className="btn btn-ghost" onClick={() => scrollTo("how")}>
                How it works ↓
              </button>
            </div>
            <div className="fine mono">Free to download · your first hour is free</div>
          </div>

          <div className="hero-vis">
            <div className="phone-cap mono">📱 The real editor, live on your phone</div>
            <div className="vstage">
              <FlagshipLiveEt bare controls defaultMode="phone" />
            </div>
          </div>
        </div>
      </header>

      {/* The demo reel. This page sells the phone → Premiere trick, and the video is the
          only proof of it a stranger can get without installing anything. */}
      <section className="watch" id="watch">
        <div className="wrap">
          <div className="sec-head center">
            <span className="eyebrow">See it happen</span>
            <h2>The CherryClipz spreadsheet, inside Premiere Pro.</h2>
            <p>Controlled from your phone. Tick a line, the clip lands on your timeline.</p>
          </div>
          <div className="watch-frame">
            <iframe
              src={`https://www.youtube.com/embed/${DEMO_YOUTUBE_ID}`}
              title="CherryClipz for Premiere Pro — demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* IN PREMIERE — full-width proof: tick on your phone → it lands on the Premiere timeline */}
      <section className="inpremiere" id="inpremiere">
        <div className="wrap-wide">
          <div className="sec-head center">
            <span className="eyebrow">Phone → Premiere, live</span>
            <h2>Tick a clip on your phone. It lands in Premiere.</h2>
            <p>
              No cables, no exporting. Pick a clip in the spreadsheet on your phone and it drops onto your
              Premiere Pro timeline as native, full-quality media — in about a second.
            </p>
          </div>
          <FlagshipPhoneToTimeline />
        </div>
      </section>

      {/* HOW IT WORKS — condensed, three quick steps (right after the "In Premiere" magic, so people
          understand the flow before they're asked to Choose / Try it) */}
      <section className="how how-compact" id="how">
        <div className="wrap">
          <div className="how-row">
            <span className="how-eyebrow eyebrow">How it works</span>
            <div className="how-steps">
              <div className="hstep">
                <span className="hk mono">01</span>
                <span className="ht">Open the CherryClipz panel, docked inside Premiere Pro.</span>
              </div>
              <div className="hstep">
                <span className="hk mono">02</span>
                <span className="ht">Flip on phone control and open the link on your phone.</span>
              </div>
              <div className="hstep">
                <span className="hk mono">03</span>
                <span className="ht">Tick a clip — it lands on your timeline as native media, live.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PATH — it's already cut; you just pick (right under the hero) */}
      <section className="ways" id="ways">
        <div className="wrap">
          <div className="ways-head">
            <span className="eyebrow">It's already cut for you · every quote is a clip</span>
            <h2 className="choose-hero">
              Choose<span className="cw-dot">.</span>
            </h2>
            <p className="choose-sub">
              Pick the clips yourself — or hand the AI a prompt and let it cut. Same clips, same sheet, switch any time.
            </p>
          </div>
          <div className="pills">
            <div className="way">
              <span className="way-tag red">Human Choice</span>
              <div
                className="pill red"
                role="button"
                tabIndex={0}
                onClick={() => goTryAnd("cc-demo-select")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goTryAnd("cc-demo-select");
                  }
                }}
              >
                <span className="sheen" aria-hidden />
                <div className="pill-badge red">
                  <img className="badge-logo" src={logo} alt="" height={26} style={{ width: "auto" }} />
                </div>
                <h3>CherryPick</h3>
                <div className="pill-kind red">The personal way · you cut it</div>
                <p>You choose the clips and the order — filter, search, trim and reorder by hand. The judgment stays yours.</p>
                <div className="pill-demo">
                  <SelectClipsLoop />
                </div>
                <span className="pill-cta red">Pick by hand ↑</span>
              </div>
            </div>
            <div className="way">
              <span className="way-tag pl">AI Prompt</span>
              <div
                className="pill purple"
                role="button"
                tabIndex={0}
                onClick={() => goTryAnd("cc-open-blackcherry")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goTryAnd("cc-open-blackcherry");
                  }
                }}
              >
                <span className="sheen" aria-hidden />
                <div className="pill-badge purple">
                  <img className="badge-logo-purple" src={logoPurple} alt="" height={28} style={{ width: "auto" }} />
                </div>
                <h3>
                  Black Cherry <span className="ai-badge">AI</span>
                </h3>
                <div className="pill-kind pl">The AI way · you prompt it</div>
                <p>Give it a prompt — Black Cherry AI picks, orders and scores the clips for you, on your own key.</p>
                <div className="pill-demo">
                  <BlackCherryLoop />
                </div>
                <span className="pill-cta pl">Ask Black Cherry ↑</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRY IT — the real pick → timeline */}
      <section className="try" id="try">
        <div className="wrap">
          <div className="sec-head">
            <span className="eyebrow">Try it — the real editor</span>
            <h2>
              Every quote, already cut into a clip.
              <br />
              Cherry-pick the ones you want.
            </h2>
            <p>
              This is the <b>desktop editor</b> the plugin runs inside Premiere Pro. Tick clips and they land on the
              live timeline below — then bring it to your own footage.
            </p>
            <div className="try-cta">
              <span className="deskpill mono big">🖥 Desktop view</span>
              <span className="deskpill mono">🔍 Filter, sort &amp; search — it's a real spreadsheet</span>
              <button className="btn btn-cherry sm" onClick={goHome}>
                Upload your own video
              </button>
            </div>
          </div>
          <FlagshipEtTimeline />
        </div>
      </section>

      {/* PRICING */}
      <section className="price" id="pricing">
        <div className="wrap">
          <span className="eyebrow" style={{ display: "block", textAlign: "center" }}>
            Pricing
          </span>
          <h2 className="price-h">Free to start. Pro when you're rolling.</h2>
          <p className="price-sub">
            Every quote and every silence becomes its own clip. Start free, on the web or in
            Premiere Pro.
          </p>
          <div className="cards">
            <div className="card">
              <div className="plan mono">Free</div>
              <div className="scope mono">Web only</div>
              <div className="amt">
                $0<span> / mo</span>
              </div>
              <div className="desc">Everything you need to try the whole thing.</div>
              {/* MUST MATCH /pricing/ (SubscriptionPlans.jsx DEFAULT_PLAN_FEATURES) and the
                  ResourcesByPlan rows in admin. This card used to say 5 hours while /pricing/
                  said 2, and listed phone→Premiere and shareable links as Pro perks that are
                  not gated to Pro at all. Two pricing pages disagreeing is worse than either. */}
              <ul>
                <li>
                  <CheckSvg />1 free hour of transcription to start
                </li>
                <li>
                  <CheckSvg />10 GB · 50 exports a month
                </li>
                <li>
                  <CheckSvg />The full editor on the web
                </li>
                <li>
                  <CheckSvg />Black Cherry AI — personas, filler, scoring
                </li>
                <li>
                  <CheckSvg />Share your clip sheet in a browser — no login
                </li>
              </ul>
              <button className="btn btn-ghost" onClick={() => scrollTo("try")}>
                Try it free
              </button>
            </div>
            <div className="card">
              <div className="plan mono">Creator</div>
              <div className="scope mono">Web only</div>
              <div className="amt">
                $10<span> / mo</span>
              </div>
              <div className="desc">For anyone cutting more than an hour.</div>
              <ul>
                <li>
                  <CheckSvg />5 hours of transcription
                </li>
                <li>
                  <CheckSvg />20 GB · 100 exports a month
                </li>
                <li>
                  <CheckSvg />Everything in Free, five times the hours
                </li>
                <li>
                  <CheckSvg />No Premiere plugin — that is Pro
                </li>
              </ul>
              <button className="btn btn-ghost" onClick={goPricing}>
                Get Creator
              </button>
            </div>
            <div className="card pro">
              <div className="plan mono">Pro</div>
              <div className="scope mono on">Web + Premiere plugin</div>
              <div className="amt">
                $29<span> / mo</span>
              </div>
              <div className="desc">For creators shipping every week.</div>
              <ul>
                <li>
                  <CheckSvg />The Premiere plugin — CherryPick to your timeline
                </li>
                <li>
                  <CheckSvg />Drive the plugin from your phone, live
                </li>
                <li>
                  <CheckSvg />15 hours of transcription
                </li>
                <li>
                  <CheckSvg />100 GB · 500 exports a month
                </li>
                <li>
                  <CheckSvg />Priority support, usually same day
                </li>
              </ul>
              <button className="btn btn-cherry" onClick={goPricing}>
                Go Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FORMATS — big screens & foldables (bottom of the page) */}
      <section className="formats" id="formats">
        <div className="wrap">
          <div className="sec-head center">
            <span className="eyebrow">The format of the future</span>
            <h2>The future of cherry-picking, on your tablet.</h2>
            <p>The same sheet, the same picks — it reflows to whatever you open it on. The full desktop editor on an iPad or an unfolded Fold; the mobile sheet when it folds shut.</p>
          </div>
          <FlagshipFormats />
        </div>
      </section>

      {/* CLOSE */}
      <section className="close">
        <div className="wrap">
          <div className="lockup center">
            <img className="cc-mark" src={logo} alt="CherryClipz" height={40} style={{ width: "auto" }} />
            <span className="plus">+</span>
            <PrLogo />
          </div>
          <h2>Cut Premiere from your phone.</h2>
          <p>Open the link, tick clips, watch them land on your timeline. The plugin is free to download.</p>
          {/* The panel download had no route in from any public page — you had to be sent a
              link. This is the one place people already arrive wanting exactly that. */}
          <div className="close-cta">
            <button className="btn btn-cherry" onClick={goPluginDownload}>
              Download the panel
            </button>
            <button className="btn btn-ghost" onClick={goPricing}>
              See pricing
            </button>
          </div>
          <p className="close-fine mono">macOS · Premiere Pro 2022 or newer</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="wrap foot">
          <button className="brand brand-btn" onClick={goHome} aria-label="CherryClipz home">
            <Brand size={24} />
          </button>
          <div className="fl">
            <button className="linkbtn" onClick={() => scrollTo("how")}>
              How it works
            </button>
            <button className="linkbtn" onClick={() => scrollTo("try")}>
              Try it
            </button>
            <button className="linkbtn" onClick={() => scrollTo("pricing")}>
              Pricing
            </button>
            <button className="linkbtn" onClick={goHome}>
              Home
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PLUGIN_CSS = `
.ccpp[data-theme="light"]{
  --bg:#f4f1ee; --surface:#ffffff; --surface-2:#faf7f4; --line:#e9e3dd; --line-2:#ddd5ce;
  --ink:#181315; --muted:#75696e; --faint:#a89ea3; --cherry:#e01e49; --cherry-soft:#ec5578;
  --shadow:0 30px 60px -34px rgba(60,20,30,.28);
}
.ccpp[data-theme="dark"]{
  --bg:#0e0b0d; --surface:#161114; --surface-2:#1b1418; --line:#282025; --line-2:#38292f;
  --ink:#f5edf0; --muted:#a99ca2; --faint:#7a6d73; --cherry:#ec2b57; --cherry-soft:#ff6f8f;
  --shadow:0 40px 80px -40px rgba(0,0,0,.8);
}
.ccpp{
  --sans: system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --mono: ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  position:relative; min-height:100vh; background:var(--bg); color:var(--ink);
  font-family:var(--sans); line-height:1.5; -webkit-font-smoothing:antialiased;
  transition:background .35s ease, color .35s ease;
}
@media (prefers-reduced-motion: reduce){ .ccpp{transition:none} }
.ccpp *{box-sizing:border-box}
.ccpp .wrap{max-width:1140px;margin:0 auto;padding:0 28px;position:relative}
.ccpp .mono{font-family:var(--mono)}
.ccpp .linkbtn{font-family:inherit;background:none;border:none;padding:0;margin:0;cursor:pointer;color:inherit}
.ccpp .brand-btn{background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;gap:10px}
.ccpp h1,.ccpp h2,.ccpp h3{margin:0;font-weight:770;letter-spacing:-0.03em;line-height:1.03;text-wrap:balance}
.ccpp .eyebrow{font-family:var(--mono);font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--faint)}
.ccpp .ink-cherry{color:var(--cherry)}

.ccpp .btn{display:inline-flex;align-items:center;gap:8px;font-family:var(--sans);font-weight:650;font-size:15px;border-radius:999px;padding:13px 24px;border:1px solid transparent;cursor:pointer;transition:transform .14s ease, background .2s ease, border-color .2s ease, filter .2s ease;background:transparent;color:var(--ink)}
.ccpp .btn-cherry{background:var(--cherry);color:#fff}
.ccpp .btn-cherry:hover{transform:translateY(-1px);filter:brightness(1.06)}
.ccpp .btn-ghost{border-color:var(--line-2);color:var(--ink)}
.ccpp .btn-ghost:hover{border-color:var(--cherry)}
@media (prefers-reduced-motion: reduce){ .ccpp .btn:hover{transform:none} }
.ccpp :focus-visible{outline:2px solid var(--cherry);outline-offset:3px;border-radius:8px}

.ccpp nav{position:sticky;top:0;z-index:20;background:color-mix(in srgb, var(--bg) 82%, transparent);backdrop-filter:blur(12px);border-bottom:1px solid var(--line)}
.ccpp .nav-in{display:flex;align-items:center;gap:22px;height:70px}
.ccpp .cherry-mark{flex:none;display:block}
.ccpp .wordmark{font-weight:820;font-size:21px;letter-spacing:-.02em;color:var(--ink)}
.ccpp .wordmark .ck{color:var(--cherry)}
.ccpp .nav-links{display:flex;gap:22px;margin-left:14px}
.ccpp .nav-links .linkbtn{font-size:14px;color:var(--muted);font-weight:550}
.ccpp .nav-links .linkbtn:hover{color:var(--ink)}
.ccpp .nav-right{margin-left:auto;display:flex;align-items:center;gap:14px}
.ccpp .toggle{display:inline-flex;align-items:center;border:1px solid var(--line-2);border-radius:999px;padding:3px;background:var(--surface-2);cursor:pointer}
.ccpp .toggle span{display:grid;place-items:center;width:30px;height:26px;border-radius:999px;color:var(--faint)}
.ccpp .toggle span svg{width:15px;height:15px;stroke:currentColor;fill:none;display:block}
.ccpp[data-theme="light"] .toggle .sun{background:var(--surface);color:var(--cherry);box-shadow:0 1px 3px rgba(0,0,0,.08)}
.ccpp[data-theme="dark"] .toggle .moon{background:#241a1f;color:var(--cherry-soft)}
@media (max-width:900px){ .ccpp .nav-links{display:none} }

.ccpp .lockup{display:flex;align-items:center;gap:18px;margin-bottom:18px}
.ccpp .lockup.center{justify-content:center;margin-bottom:22px}
.ccpp .lockup .plus{font-size:34px;color:var(--faint);font-weight:300}
.ccpp .prlogo{display:inline-grid;place-items:center;width:52px;height:52px;border-radius:12px;background:#2b0a52;color:#c9b3ff;font-weight:800;font-size:23px;letter-spacing:-.02em;border:1px solid #4a2a7a}

.ccpp .hero{padding:52px 0 44px}
.ccpp .hero-grid{display:grid;grid-template-columns:0.86fr 1.14fr;gap:44px;align-items:center}
.ccpp .hero-copy{min-width:0}
.ccpp .hero-copy .eyebrow{display:inline-block;margin-bottom:14px}
.ccpp .hero h1{font-size:clamp(36px,4.6vw,60px)}
.ccpp .hero .pocket{margin:14px 0 0;font-size:13px;font-weight:700;letter-spacing:.02em;color:var(--cherry)}
.ccpp .hero .sub{max-width:460px;margin:16px 0 0;color:var(--muted);font-size:clamp(15px,1.4vw,17px)}
.ccpp .hero .cta{display:flex;gap:12px;margin-top:26px;flex-wrap:wrap}
.ccpp .hero .fine{margin-top:16px;font-size:12px;color:var(--faint)}
.ccpp .hero-vis{min-width:0}
.ccpp .vtoggle{display:inline-flex;gap:3px;padding:3px;border:1px solid var(--line-2);border-radius:999px;background:var(--surface-2);margin:0 auto 16px;display:flex;width:fit-content}
.ccpp .vtab{font-family:var(--sans);font-size:13px;font-weight:600;color:var(--muted);border:none;background:transparent;border-radius:999px;padding:8px 16px;cursor:pointer}
.ccpp .vtab.on{color:#fff;background:var(--cherry)}
.ccpp .vstage{display:flex;justify-content:center;align-items:flex-start;min-height:520px}
.ccpp .phone-cap{text-align:center;font-size:12px;color:var(--faint);margin-bottom:14px;letter-spacing:.04em}
/* In-Premiere proof — a full-width band (wider than the standard wrap) so the phone→timeline mock breathes */
.ccpp .wrap-wide{max-width:1280px;margin:0 auto;padding:0 28px;position:relative}
.ccpp .inpremiere{background:var(--surface-2)}
.ccpp .inpremiere .sec-head{margin-bottom:40px}
.ccpp .prem-realimg{width:100%;height:auto;display:block;border-radius:12px;border:1px solid var(--line);box-shadow:var(--shadow)}
.ccpp .linkbar{display:flex;align-items:center;gap:8px;max-width:520px;margin:18px auto 0;padding:8px 10px;background:#1a1130;border:1px solid #3a2a5a;border-radius:9px}
.ccpp .pll-lbl{font-size:11px;color:#c9b8ff;white-space:nowrap}
.ccpp .pll-in{flex:1;min-width:0;font-size:11px;padding:6px 8px;background:#0f0a1e;color:#e8e0ff;border:1px solid #3a2a5a;border-radius:5px}
.ccpp .pll-copy{flex:none;font-size:11px;font-weight:700;color:#fff;background:#7b4bd8;border:none;border-radius:5px;padding:7px 14px;cursor:pointer}
.ccpp .pll-copy:hover{background:#8f63e6}
@media (max-width:960px){ .ccpp .hero-grid{grid-template-columns:1fr;gap:32px} }

.ccpp section{padding:76px 0;border-top:1px solid var(--line)}
.ccpp .sec-head{max-width:600px;margin-bottom:34px}
.ccpp .sec-head h2{font-size:clamp(26px,3.4vw,40px);margin-top:12px}
.ccpp .sec-head p{color:var(--muted);font-size:16px;margin-top:12px}
.ccpp .how{background:var(--surface-2)}
/* condensed how-it-works (a single quiet row) */
.ccpp .how-compact{padding:38px 0}
.ccpp .how-row{display:flex;align-items:center;gap:24px;flex-wrap:wrap;justify-content:center}
.ccpp .how-eyebrow{flex:none}
.ccpp .how-steps{display:flex;gap:28px;flex-wrap:wrap;justify-content:center}
.ccpp .hstep{display:flex;align-items:center;gap:9px;max-width:300px}
.ccpp .hk{font-size:12px;color:var(--cherry);letter-spacing:.1em;font-weight:700;flex:none}
.ccpp .ht{font-size:13px;color:var(--muted);line-height:1.4}
.ccpp .steps{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.ccpp .step{background:var(--surface);border:1px solid var(--line);border-radius:14px;padding:24px}
.ccpp .step .k{font-size:12px;color:var(--cherry);letter-spacing:.1em}
.ccpp .step h3{font-size:19px;margin:10px 0 6px}
.ccpp .step p{margin:0;color:var(--muted);font-size:14px}
.ccpp .feats{display:flex;flex-wrap:wrap;gap:10px 22px;margin-top:26px}
.ccpp .feat{display:inline-flex;align-items:center;gap:8px;font-size:14px;color:var(--muted)}
.ccpp .feat svg{width:15px;height:15px;stroke:var(--cherry);fill:none;flex:none;display:block}
@media (max-width:760px){ .ccpp .steps{grid-template-columns:1fr} }

.ccpp .try{background:var(--surface-2)}
.ccpp .try-cta{display:flex;align-items:center;gap:12px;margin-top:20px;flex-wrap:wrap}
.ccpp .deskpill{font-size:12px;color:var(--muted);border:1px solid var(--line-2);border-radius:999px;padding:7px 13px;background:var(--surface)}
.ccpp .deskpill.big{font-size:14px;font-weight:700;color:var(--ink);border-color:var(--cherry);padding:9px 17px}
.ccpp .btn.sm{padding:10px 18px;font-size:14px}
.ccpp .try .sec-head b{color:var(--ink);font-weight:750}

/* formats — the 3D device wall */
.ccpp .formats{overflow:hidden}
.ccpp .sec-head.center{max-width:640px;margin-left:auto;margin-right:auto;text-align:center}

/* pricing */
.ccpp .price{text-align:center}
.ccpp .price-h{margin-top:14px;font-size:clamp(28px,3.6vw,42px)}
.ccpp .price-sub{color:var(--muted);font-size:16px;margin-top:12px}
.ccpp .cards{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;max-width:1040px;margin:40px auto 0;text-align:left}
.ccpp .card{background:var(--surface);border:1px solid var(--line);border-radius:18px;padding:30px}
.ccpp .card.pro{border-color:var(--cherry);box-shadow:0 0 0 1px var(--cherry), var(--shadow)}
.ccpp .card .plan{font-size:12px;letter-spacing:.16em;text-transform:uppercase;color:var(--faint)}
/* Where the plan works — the one difference people shouldn't have to infer from bullets. */
.ccpp .card .scope{display:inline-block;margin-top:8px;font-size:11px;font-weight:700;letter-spacing:.06em;padding:4px 10px;border-radius:999px;border:1px solid var(--line-2);color:var(--muted)}
.ccpp .card .scope.on{background:var(--cherry);border-color:var(--cherry);color:#fff}
.ccpp .card .amt{font-size:42px;font-weight:780;letter-spacing:-.03em;margin:12px 0 2px}
.ccpp .card .amt span{font-size:15px;font-weight:500;color:var(--faint)}
.ccpp .card .desc{color:var(--muted);font-size:14px;margin-bottom:20px}
.ccpp .card ul{list-style:none;margin:0 0 24px;padding:0;display:flex;flex-direction:column;gap:10px}
.ccpp .card li{display:flex;gap:9px;font-size:14px;color:var(--muted);align-items:flex-start}
.ccpp .card li svg{width:15px;height:15px;stroke:var(--cherry);fill:none;flex:none;margin-top:2px;display:block}
.ccpp .card .btn{width:100%;justify-content:center}
@media (max-width:900px){ .ccpp .cards{grid-template-columns:1fr;max-width:420px} }

.ccpp .watch{background:var(--surface-2)}
.ccpp .watch-frame{position:relative;width:100%;max-width:900px;margin:0 auto;aspect-ratio:16/9;border-radius:16px;overflow:hidden;border:1px solid var(--line);box-shadow:var(--shadow);background:#000}
.ccpp .watch-frame iframe{position:absolute;inset:0;width:100%;height:100%;border:0;display:block}
.ccpp .close{text-align:center}
.ccpp .close h2{font-size:clamp(28px,4vw,48px)}
.ccpp .close p{color:var(--muted);font-size:17px;margin:16px 0 28px}
.ccpp .close-cta{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
.ccpp .close-fine{margin-top:16px;font-size:12px;color:var(--faint)}
.ccpp footer{border-top:1px solid var(--line);padding:34px 0}
.ccpp .foot{display:flex;align-items:center;gap:20px;flex-wrap:wrap}
.ccpp .foot .fl{margin-left:auto;display:flex;gap:22px;font-family:var(--mono);font-size:12px;color:var(--faint)}
.ccpp .foot .fl .linkbtn{color:var(--faint)}
.ccpp .foot .fl .linkbtn:hover{color:var(--muted)}

/* Premiere screenshot placeholder (no fake mockup — real shot drops in) */
.ccpp .prem-ph{width:100%;min-height:380px;border:2px dashed var(--line-2);border-radius:14px;background:var(--surface-2);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;text-align:center;padding:30px}
.ccpp .prem-ph-icon{opacity:.95}
.ccpp .prem-ph-title{font-weight:700;font-size:18px;color:var(--ink)}
.ccpp .prem-ph-code{font-size:13px;color:var(--cherry);background:color-mix(in srgb, var(--cherry) 12%, transparent);padding:6px 12px;border-radius:8px}
.ccpp .prem-ph-note{font-size:13px;color:var(--muted);max-width:340px}

/* two-ways pills (cherrypick vs Black Cherry) */
.ccpp .ways-head{text-align:center;max-width:660px;margin:0 auto 40px}
.ccpp .ways-head h2{font-size:clamp(28px,3.6vw,44px);margin-top:12px}
.ccpp .ways-head p{color:var(--muted);font-size:16px;margin-top:12px}
.ccpp .choose-word{color:var(--cherry);position:relative;white-space:nowrap}
.ccpp .choose-word::after{content:"";position:absolute;left:-2px;right:-2px;bottom:2px;height:36%;background:color-mix(in srgb, var(--cherry) 20%, transparent);border-radius:3px;z-index:-1}
/* the "Choose." moment — one big word */
.ccpp .choose-hero{font-size:clamp(56px,9vw,104px);font-weight:830;letter-spacing:-.045em;line-height:.95;margin:8px 0 0}
.ccpp .cw-dot{color:var(--cherry)}
.ccpp .choose-sub{color:var(--muted);font-size:17px;margin:16px auto 0;max-width:520px}
.ccpp .pills{display:grid;grid-template-columns:1fr 1fr;gap:48px;max-width:980px;margin:0 auto}
.ccpp .way{display:flex;flex-direction:column;align-items:stretch;height:100%}
.ccpp .way .pill{flex:1 1 auto} /* both cards fill the row → equal height (symmetry) */
.ccpp .way-tag{align-self:center;font-family:var(--mono);font-size:15px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:#fff;padding:8px 26px;border-radius:999px;margin-bottom:16px}
.ccpp .way-tag.red{background:var(--cherry);box-shadow:0 10px 26px -12px rgba(224,30,73,.8)}
.ccpp .way-tag.pl{background:#7c3aed;box-shadow:0 10px 26px -12px rgba(124,58,237,.8)}
.ccpp .pill{position:relative;overflow:hidden;cursor:pointer;width:100%;font-family:inherit;color:inherit;-webkit-appearance:none;appearance:none;border:1px solid var(--line-2);border-radius:16px;padding:30px 28px;background:var(--surface);text-align:center;display:flex;flex-direction:column;align-items:center;transition:transform .22s ease, box-shadow .3s ease, border-color .3s ease}
.ccpp .pill>.pill-badge,.ccpp .pill>h3,.ccpp .pill>.pill-kind,.ccpp .pill>p,.ccpp .pill>.pill-demo,.ccpp .pill>.pill-cta{position:relative;z-index:1}
.ccpp .pill>p{flex:1 1 auto} /* description grows → the demo + CTA sit at the same height in both cards (aligned) */
.ccpp .pill>.pill-demo{width:100%}
.ccpp .pill-demo{margin-top:18px}
.ccpp .ai-badge{font-size:0.6em;font-weight:800;letter-spacing:.05em;color:#fff;background:#7c3aed;padding:2px 7px;border-radius:6px;vertical-align:middle;margin-left:5px}
.ccpp .pill:hover{transform:translateY(-4px) scale(1.015)}
.ccpp .pill:focus-visible{outline:2px solid var(--cherry);outline-offset:3px}
@media (prefers-reduced-motion: reduce){ .ccpp .pill:hover{transform:none} .ccpp .pill .sheen{display:none} }
.ccpp .pill.red{border-color:rgba(236,43,87,.45);box-shadow:0 30px 70px -50px rgba(236,43,87,.7)}
.ccpp .pill.purple{border-color:rgba(139,92,246,.45);box-shadow:0 30px 70px -50px rgba(139,92,246,.7)}
.ccpp .pill.red:hover{border-color:rgba(236,43,87,.95);box-shadow:0 26px 60px -32px rgba(236,43,87,.95), 0 0 0 1px rgba(236,43,87,.45)}
.ccpp .pill.purple:hover{border-color:rgba(139,92,246,.95);box-shadow:0 26px 60px -32px rgba(139,92,246,.95), 0 0 0 1px rgba(139,92,246,.45)}
/* light streak that sweeps across on hover — "speed / it's alive" */
.ccpp .pill .sheen{position:absolute;top:0;bottom:0;left:0;width:55%;z-index:0;pointer-events:none;transform:translateX(-170%) skewX(-14deg);background:linear-gradient(105deg, transparent 0%, rgba(255,255,255,.16) 50%, transparent 100%)}
.ccpp .pill:hover .sheen{animation:ccpp-sheen .8s ease}
@keyframes ccpp-sheen{to{transform:translateX(290%) skewX(-14deg)}}
.ccpp .pill-cta{display:inline-block;margin-top:16px;font-family:var(--mono);font-size:12px;font-weight:700;letter-spacing:.04em;opacity:.82;transition:opacity .2s ease, transform .2s ease}
.ccpp .pill-cta.red{color:var(--cherry)}
.ccpp .pill-cta.pl{color:#8b5cf6}
.ccpp .pill:hover .pill-cta{opacity:1;transform:translateY(-1px)}
.ccpp .pill-badge{width:64px;height:64px;border-radius:16px;display:grid;place-items:center;margin:0 auto 16px}
.ccpp .pill-badge.red{background:var(--cherry);box-shadow:0 12px 32px -12px rgba(236,43,87,.7)}
.ccpp .pill-badge.purple{background:#160c26;border:1px solid rgba(139,92,246,.5);box-shadow:0 12px 34px -12px rgba(139,92,246,.75)}
.ccpp .badge-logo{display:block;filter:brightness(0) invert(1)}
.ccpp .badge-logo-purple{display:block}
.ccpp .pill h3{font-size:24px;margin-bottom:6px}
.ccpp .pill-kind{font-family:var(--mono);font-size:11px;letter-spacing:.06em;text-transform:uppercase;margin-bottom:12px}
.ccpp .pill-kind.red{color:var(--cherry)}
.ccpp .pill-kind.pl{color:#8b5cf6}
.ccpp .pill p{color:var(--muted);font-size:15px;margin:0 auto;max-width:320px}
.ccpp .linknote{text-align:center;color:var(--faint);font-size:11px;margin-top:8px}
@media (max-width:720px){ .ccpp .pills{grid-template-columns:1fr} }
`;

export default PluginPage;
